import ConfirmDialog from "../components/ui/ConfirmDialog"
import { useEffect, useMemo, useState } from "react";
import Spinner, { SpinnerText } from "../components/ui/Spinner";
import ActionButton from "../components/applications/ActionButton";
import { FiCheck, FiCopy, FiEdit2, FiEye, FiFileText, FiPlus, FiRefreshCw, FiSend, FiTrash2, FiUploadCloud } from "react-icons/fi";
import AppLayout from "../layout/AppLayout"
import ApplicationCard from "../components/applications/ApplicationCard";
import PowerOfAttorneyPanel from "../components/applications/PowerOfAttorneyPanel";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import {
  getApplicationDrafts,
  deleteApplicationDraft,
  duplicateApplicationDraft,
  updateApplicationStatus,
} from "../services/applicationService";
import {
  deleteDesignApplicationDraft,
  duplicateDesignApplicationDraft,
  getDesignApplicationDrafts,
  updateDesignApplicationStatus,
} from "../services/designApplicationService";
import {
  deletePatentApplicationDraft,
  duplicatePatentApplicationDraft,
  getPatentApplicationDrafts,
  updatePatentApplicationStatus,
} from "../services/patentApplicationService";
import {
  getApplicationsAwaitingRelease,
  releaseApplication,
  markCustomerPaymentReceived,
  resubmitPaidApplication,
} from "../services/stripePaymentService";
import { retryPatOrgAkte } from "../services/patOrgAkteService";
import { formatApiError } from "../utils/formatApiError";
import { getPatentApplicationNumber } from "../utils/patentApplicationNumber";
import { getPatentProcedureLabel } from "../utils/patentProcedure";
import { useNavigate } from "react-router-dom";

interface Application {
  _id: string;
  referenceNumber: string;
  // The number the office gave the filing (EP number, EUIPO number), once there is one
  applicationNumber?: string;
  applicantName: string;
  ownerName: string;
  type?: string;
  procedureType?: "EP1001" | "EP1200";
  status:
    | "DRAFT"
    | "VALIDATED"
    | "AWAITING_RELEASE"
    | "PAYMENT_PENDING"
    | "ERROR"
    | "SUBMITTED"
    | "PAID";
  // The PatOrg file of the firm, only shown to admins
  patOrgPkey?: string;
  patOrgStatus?: "PENDING" | "SYNCED" | "ERROR";
  patOrgError?: string;
  createdAt: string;
  updatedAt: string;
  createdAtRaw: string;
  updatedAtRaw: string;
}

type ApplicationKind = "trademark" | "design" | "patent";
type TabKey = ApplicationKind | "release";

interface ReleaseItem {
  app: Application;
  kind: ApplicationKind;
  paid: boolean;
  requestedAt: string;
}

function isLockedStatus(status: Application["status"]) {
  return status === "PAID" || status === "PAYMENT_PENDING" || status === "AWAITING_RELEASE";
}

function formatDate(value: string) {
  return value ? new Date(value).toLocaleDateString() : "-";
}

function formatPersonName(person: any) {
  if (!person) return "";

  const fullName = [person.firstName, person.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || person.legalName || person.organisationName || person.company || person.email || "";
}

function formatApplicantName(app: any) {
  const applicants = Array.isArray(app.applicants) ? app.applicants : [];
  const names = applicants
    .map(formatPersonName)
    .filter(Boolean);

  if (names.length > 0) return names.join(", ");

  return "-";
}

function getOwnerName(app: any, currentUser: any) {
  const populatedOwnerName = formatPersonName(app.userId);
  if (populatedOwnerName) return populatedOwnerName;

  const ownerId = typeof app.userId === "string" ? app.userId : app.userId?._id;
  if (currentUser && ownerId && ownerId === currentUser.id) {
    return formatPersonName(currentUser);
  }

  return app.userId?.email || "-";
}

function mapApplication(app: any, currentUser: any, type?: string): Application {
  return {
    _id: app._id,
    referenceNumber: app.referenceNumber?.referenceNumber || "-",
    applicationNumber: getPatentApplicationNumber(app),
    applicantName: formatApplicantName(app),
    ownerName: getOwnerName(app, currentUser),
    type: type || app.type || app.applicationType || app.category,
    procedureType: app.procedureType,
    status: app.status,
    patOrgPkey: app.patOrgPkey,
    patOrgStatus: app.patOrgStatus,
    patOrgError: app.patOrgError,
    createdAt: formatDate(app.createdAt),
    updatedAt: formatDate(app.updatedAt),
    createdAtRaw: app.createdAt || "",
    updatedAtRaw: app.updatedAt || "",
  };
}

function getPatentPath(app: Application, action: "edit" | "view") {
  return app.procedureType === "EP1200"
    ? `/patent-applications/${app._id}/ep1200/${action}`
    : `/patent-applications/${app._id}/${action}`;
}

export default function MyApplicationsPage() {
  const [trademarkApplications, setTrademarkApplications] = useState<Application[]>([]);
  const [designApplications, setDesignApplications] = useState<Application[]>([]);
  const [patentApplications, setPatentApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState("");
  const [duplicateRequest, setDuplicateRequest] = useState<{ reference: string; kind: ApplicationKind; confirm: () => Promise<void> } | null>(null);
  const [resubmittingId, setResubmittingId] = useState<string | null>(null);
  const [patOrgBusyId, setPatOrgBusyId] = useState<string | null>(null);
  const [patOrgNote, setPatOrgNote] = useState<{ id: string; message: string } | null>(null);
  const [resubmissionError, setResubmissionError] = useState<{
    id: string;
    message: string;
  } | null>(null);

  const [activeTab, setActiveTab] = useState<TabKey>("trademark");
  const [releaseItems, setReleaseItems] = useState<ReleaseItem[]>([]);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<{ id: string; message: string } | null>(null);

  const PAGE_SIZE = 5;

  // Application whose power of attorney panel is open
  const [powerOfAttorneyOpenId, setPowerOfAttorneyOpenId] = useState<string | null>(null);
  const [trademarkPage, setTrademarkPage] = useState(1);
  const [designPage, setDesignPage] = useState(1);
  const [patentPage, setPatentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [sort, setSort] = useState<"created-desc" | "created-asc" | "updated-desc" | "updated-asc" | "reference-asc" | "reference-desc">("created-desc");

  const token = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.id;
  const currentUserFirstName = currentUser?.firstName;
  const currentUserLastName = currentUser?.lastName;
  const currentUserEmail = currentUser?.email;
  const currentUserIsAdmin = currentUser?.isAdmin;
  const isAdmin = Boolean(currentUserIsAdmin);
  const canReleaseFilings = Boolean(currentUser?.canReleaseFilings);
  const currentUserSnapshot = useMemo(
    () => currentUserId
      ? {
          id: currentUserId,
          firstName: currentUserFirstName,
          lastName: currentUserLastName,
          email: currentUserEmail,
          isAdmin: currentUserIsAdmin,
        }
      : null,
    [currentUserId, currentUserFirstName, currentUserLastName, currentUserEmail, currentUserIsAdmin],
  );
  const navigate = useNavigate();
  const isLockedForUser = (status: Application["status"]) =>
    !isAdmin && isLockedStatus(status);

  const owners = useMemo(() => Array.from(new Set([
    ...trademarkApplications, ...designApplications, ...patentApplications,
  ].map((app) => app.ownerName).filter((name) => name && name !== "-"))).sort(),
  [trademarkApplications, designApplications, patentApplications]);

  function prepareApplications(items: Application[]) {
    const needle = search.trim().toLocaleLowerCase();
    return [...items]
      .filter((app) => {
        if (ownerFilter && app.ownerName !== ownerFilter) return false;
        if (!needle) return true;
        return [app.referenceNumber, app.applicationNumber, app.applicantName, app.ownerName, app.status]
          .filter(Boolean).some((value) => value!.toLocaleLowerCase().includes(needle));
      })
      .sort((a, b) => {
        if (sort === "reference-asc" || sort === "reference-desc") {
          const result = a.referenceNumber.localeCompare(b.referenceNumber, undefined, { numeric: true });
          return sort === "reference-asc" ? result : -result;
        }
        const aValue = sort.startsWith("created") ? a.createdAtRaw : a.updatedAtRaw;
        const result = aValue.localeCompare(sort.startsWith("created") ? b.createdAtRaw : b.updatedAtRaw);
        return sort.endsWith("asc") ? result : -result;
      });
  }

  const tabs = useMemo(
    () => {
      const items: { key: TabKey; label: string; count: number }[] = [
        { key: "trademark", label: "Trademarks", count: trademarkApplications.length },
        { key: "design", label: "Designs", count: designApplications.length },
        { key: "patent", label: "Patents", count: patentApplications.length },
      ];

      if (canReleaseFilings) {
        items.push({ key: "release", label: "Awaiting release", count: releaseItems.length });
      }

      return items;
    },
    [trademarkApplications.length, designApplications.length, patentApplications.length, canReleaseFilings, releaseItems.length],
  );

  useEffect(() => {
    async function load() {
      try {
        const [trademarkData, designData, patentData] = await Promise.all([
          getApplicationDrafts(),
          getDesignApplicationDrafts(),
          getPatentApplicationDrafts(),
        ]);

        setTrademarkApplications(
          trademarkData.map((app: any) => mapApplication(app, currentUserSnapshot, "TRADEMARK")),
        );
        setDesignApplications(
          designData.map((app: any) => mapApplication(app, currentUserSnapshot, "DESIGN")),
        );
        setPatentApplications(
          patentData.map((app: any) => mapApplication(app, currentUserSnapshot, "PATENT")),
        );

        if (canReleaseFilings) {
          const waiting = await getApplicationsAwaitingRelease();
          const toItems = (list: any[], kind: ApplicationKind, type: string): ReleaseItem[] =>
            list.map((app: any) => ({
              app: mapApplication(app, currentUserSnapshot, type),
              kind,
              paid: Boolean(app.customerPaymentReceived),
              requestedAt: app.releaseRequestedAt || app.updatedAt,
            }));

          setReleaseItems(
            [
              ...toItems(waiting.trademark, "trademark", "TRADEMARK"),
              ...toItems(waiting.design, "design", "DESIGN"),
              ...toItems(waiting.patent, "patent", "PATENT"),
            ].sort((a, b) => a.requestedAt.localeCompare(b.requestedAt)),
          );
        }
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [token, currentUserSnapshot, canReleaseFilings]);

  async function handleDelete(id: string) {
    await deleteApplicationDraft(id);
    setTrademarkApplications((prev) => {
      const next = prev.filter((app) => app._id !== id);
      setTrademarkPage((page) => Math.min(page, Math.max(1, Math.ceil(next.length / PAGE_SIZE))));
      return next;
    });
  }

  async function handleDeleteDesign(id: string) {
    await deleteDesignApplicationDraft(id);
    setDesignApplications((prev) => {
      const next = prev.filter((app) => app._id !== id);
      setDesignPage((page) => Math.min(page, Math.max(1, Math.ceil(next.length / PAGE_SIZE))));
      return next;
    });
  }

  async function handleDeletePatent(id: string) {
    await deletePatentApplicationDraft(id);
    setPatentApplications((prev) => {
      const next = prev.filter((app) => app._id !== id);
      setPatentPage((page) => Math.min(page, Math.max(1, Math.ceil(next.length / PAGE_SIZE))));
      return next;
    });
  }

  function getDuplicateErrorMessage(error: unknown) {
    if (error instanceof Error) return error.message;
    return "The application could not be duplicated. Please try again.";
  }

  async function handleDuplicateTrademark(id: string) {
    if (duplicatingId) return;

    try {
      setDuplicatingId(id);
      setDuplicateError("");
      const duplicated = mapApplication(await duplicateApplicationDraft(id), currentUserSnapshot, "TRADEMARK");
      setTrademarkApplications((prev) => [duplicated, ...prev]);
      setTrademarkPage(1);
    } catch (error) {
      setDuplicateError(getDuplicateErrorMessage(error));
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleDuplicateDesign(id: string) {
    if (duplicatingId) return;

    try {
      setDuplicatingId(id);
      setDuplicateError("");
      const duplicated = mapApplication(await duplicateDesignApplicationDraft(id), currentUserSnapshot, "DESIGN");
      setDesignApplications((prev) => [duplicated, ...prev]);
      setDesignPage(1);
    } catch (error) {
      setDuplicateError(getDuplicateErrorMessage(error));
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleDuplicatePatent(id: string) {
    if (duplicatingId) return;

    try {
      setDuplicatingId(id);
      setDuplicateError("");
      const duplicated = mapApplication(await duplicatePatentApplicationDraft(id), currentUserSnapshot, "PATENT");
      setPatentApplications((prev) => [duplicated, ...prev]);
      setPatentPage(1);
    } catch (error) {
      setDuplicateError(getDuplicateErrorMessage(error));
    } finally {
      setDuplicatingId(null);
    }
  }

  async function markApplicationPaid(id: string) {
    const updated = await updateApplicationStatus(id, "PAID");
    setTrademarkApplications((prev) =>
      prev.map((app) =>
        app._id === id ? { ...app, status: updated.status } : app,
      ),
    );
  }

  async function markDesignApplicationPaid(id: string) {
    const updated = await updateDesignApplicationStatus(id, "PAID");
    setDesignApplications((prev) =>
      prev.map((app) =>
        app._id === id ? { ...app, status: updated.status } : app,
      ),
    );
  }

  async function markPatentApplicationPaid(id: string) {
    const updated = await updatePatentApplicationStatus(id, "PAID");
    setPatentApplications((prev) =>
      prev.map((app) =>
        app._id === id ? { ...app, status: updated.status } : app,
      ),
    );
  }

  async function handleRetryPatOrg(kind: ApplicationKind, id: string) {
    setPatOrgBusyId(id);
    setPatOrgNote(null);

    const apply = (patch: Partial<Application>) => {
      const update = (items: Application[]) =>
        items.map((app) => (app._id === id ? { ...app, ...patch } : app));

      if (kind === "trademark") {
        setTrademarkApplications(update);
      } else if (kind === "design") {
        setDesignApplications(update);
      } else {
        setPatentApplications(update);
      }
    };

    try {
      const result = await retryPatOrgAkte(kind, id);

      if (result.status === "SYNCED") {
        apply({ patOrgStatus: "SYNCED", patOrgError: undefined });
      } else if (result.status === "ERROR") {
        apply({ patOrgStatus: "ERROR", patOrgError: result.message });
      } else {
        // Not tried: the kind is switched off or the application is not paid
        setPatOrgNote({ id, message: result.message || "PatOrg did not create the file." });
      }
    } catch (error) {
      apply({ patOrgStatus: "ERROR", patOrgError: formatApiError(error, "The PatOrg file could not be created.") });
    } finally {
      setPatOrgBusyId(null);
    }
  }

  async function handleResubmit(kind: ApplicationKind, id: string) {
    if (
      !window.confirm(
        "Resubmit this corrected application? No additional payment will be taken.",
      )
    ) {
      return;
    }

    setResubmittingId(id);
    setResubmissionError(null);

    try {
      const result = await resubmitPaidApplication({
        applicationType: kind,
        applicationId: id,
      });
      const updateStatus = (items: Application[]) =>
        items.map((app) =>
          app._id === id ? { ...app, status: result.status } : app,
        );

      if (kind === "trademark") {
        setTrademarkApplications(updateStatus);
      } else if (kind === "design") {
        setDesignApplications(updateStatus);
      } else {
        setPatentApplications(updateStatus);
      }
    } catch (error) {
      setResubmissionError({
        id,
        message: formatApiError(error, "The application could not be resubmitted."),
      });
    } finally {
      setResubmittingId(null);
    }
  }

  function getApplicationPath(app: Application, kind: ApplicationKind, action: "edit" | "view") {
    return kind === "trademark"
      ? `/applications/${app._id}/${action}`
      : kind === "design"
        ? `/design-applications/${app._id}/${action}`
        : getPatentPath(app, action);
  }

  async function handlePaymentReceived(item: ReleaseItem) {
    setReleasingId(item.app._id);
    setReleaseError(null);

    try {
      await markCustomerPaymentReceived({
        applicationType: item.kind,
        applicationId: item.app._id,
      });
      setReleaseItems((prev) =>
        prev.map((entry) =>
          entry.app._id === item.app._id ? { ...entry, paid: true } : entry,
        ),
      );
    } catch (error) {
      setReleaseError({
        id: item.app._id,
        message: formatApiError(error, "The payment could not be recorded."),
      });
    } finally {
      setReleasingId(null);
    }
  }

  async function handleRelease(item: ReleaseItem) {
    if (
      !window.confirm(
        "Submit this application for filing now? The filing report is sent to the customer afterwards.",
      )
    ) {
      return;
    }

    setReleasingId(item.app._id);
    setReleaseError(null);

    try {
      await releaseApplication({
        applicationType: item.kind,
        applicationId: item.app._id,
      });
      setReleaseItems((prev) => prev.filter((entry) => entry.app._id !== item.app._id));
    } catch (error) {
      setReleaseError({
        id: item.app._id,
        message: formatApiError(error, "The application could not be filed."),
      });
    } finally {
      setReleasingId(null);
    }
  }

  function renderReleaseApplications() {
    if (loading) return <p className="text-gray-500"><SpinnerText>Loading applications...</SpinnerText></p>;
    if (releaseItems.length === 0) {
      return <p className="text-gray-500">No applications are waiting for release.</p>;
    }

    return (
      <div className="flex flex-col gap-4">
        {releaseItems.map((item) => (
          <ApplicationCard
            key={item.app._id}
            kind={item.kind}
            reference={item.app.referenceNumber}
            subtitle={item.kind.charAt(0).toUpperCase() + item.kind.slice(1)}
            status="AWAITING_RELEASE"
            fields={[
              { label: "Applicant", value: item.app.applicantName },
              { label: "Ordering party", value: item.app.ownerName },
              {
                label: "Payment",
                value: (
                  <span className={item.paid ? "text-green-700" : "text-amber-700"}>
                    {item.paid ? "Received" : "Invoice, payment outstanding"}
                  </span>
                ),
              },
              { label: "Waiting since", value: formatDate(item.requestedAt) },
            ]}
            actions={
              <>
                <ActionButton
                  label="Review"
                  icon={<FiEye size={16} />}
                  className="text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
                  onClick={() => navigate(getApplicationPath(item.app, item.kind, "view"))}
                />
                {!item.paid && (
                  <ActionButton
                    label="Payment received"
                    icon={<FiCheck size={16} />}
                    disabled={releasingId === item.app._id}
                    className="text-amber-700 border border-amber-300 bg-white hover:bg-amber-50 disabled:opacity-60"
                    onClick={() => handlePaymentReceived(item)}
                  />
                )}
                <ActionButton
                  label={releasingId === item.app._id ? "Filing..." : "Submit for filing"}
                  icon={releasingId === item.app._id ? <Spinner size={16} /> : <FiSend size={16} />}
                  disabled={releasingId === item.app._id || !item.paid}
                  title={item.paid ? undefined : "The payment has to be received first"}
                  className="text-green-700 border border-green-300 bg-white hover:bg-green-50 disabled:opacity-60"
                  onClick={() => handleRelease(item)}
                />
              </>
            }
          >
            {item.app.status === "ERROR" && (
              <p className="mt-4 text-sm text-orange-600">
                The last filing attempt failed. You can try again.
              </p>
            )}
            {releaseError?.id === item.app._id && (
              <p className="mt-3 text-sm text-red-700">{releaseError.message}</p>
            )}
          </ApplicationCard>
        ))}
      </div>
    );
  }

  function renderApplicationCard(
    app: Application,
    kind: ApplicationKind,
    onPaid: (id: string) => Promise<void>,
    onDelete: (id: string) => Promise<void>,
    onDuplicate: (id: string) => Promise<void>,
  ) {
    const action = isLockedForUser(app.status) ? "view" : "edit";
    const targetPath =
      kind === "trademark"
        ? `/applications/${app._id}/${action}`
        : kind === "design"
          ? `/design-applications/${app._id}/${action}`
          : getPatentPath(app, action);
    const isDuplicating = duplicatingId === app._id;

    return (
      <ApplicationCard
        key={app._id}
        kind={kind}
        reference={app.referenceNumber}
        subtitle={
          kind === "patent"
            ? `Patent · ${getPatentProcedureLabel(app.procedureType)}`
            : kind.charAt(0).toUpperCase() + kind.slice(1)
        }
        status={app.status}
        fields={[
          { label: "Applicant", value: app.applicantName },
          { label: "Ordering party", value: app.ownerName },
          ...(app.applicationNumber ? [{ label: "Application number", value: app.applicationNumber }] : []),
          { label: "Created", value: app.createdAt },
          { label: "Last updated", value: app.updatedAt },
          ...(isAdmin && app.status === "PAID"
            ? [
                {
                  label: "PatOrg file",
                  wide: true,
                  value:
                    app.patOrgStatus === "SYNCED" ? (
                      <span className="text-green-700">{app.patOrgPkey || "created"}</span>
                    ) : app.patOrgStatus === "ERROR" ? (
                      <span className="text-red-700">failed{app.patOrgError ? `: ${app.patOrgError}` : ""}</span>
                    ) : (
                      <span className="text-appMuted">not created</span>
                    ),
                },
              ]
            : []),
        ]}
        actions={
          <>
            {isAdmin && app.status === "PAID" && app.patOrgStatus !== "SYNCED" && (
              <ActionButton
                label={patOrgBusyId === app._id ? "Creating PatOrg file..." : "Create PatOrg file"}
                icon={patOrgBusyId === app._id ? <Spinner size={16} /> : <FiUploadCloud size={16} />}
                disabled={patOrgBusyId === app._id}
                className="text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60"
                onClick={() => handleRetryPatOrg(kind, app._id)}
              />
            )}
            {isAdmin && app.status === "ERROR" && (
              <ActionButton
                label={resubmittingId === app._id ? "Resubmitting..." : "Resubmit (already paid)"}
                icon={resubmittingId === app._id ? <Spinner size={16} /> : <FiRefreshCw size={16} />}
                disabled={resubmittingId === app._id}
                className="text-amber-700 border border-amber-300 bg-white hover:bg-amber-50 disabled:opacity-60"
                onClick={() => handleResubmit(kind, app._id)}
              />
            )}
            {isAdmin && app.status === "PAYMENT_PENDING" && (
              <ActionButton
                label="Application PAID"
                icon={<FiCheck size={16} />}
                className="text-green-700 border border-green-300 bg-white hover:bg-green-50"
                onClick={() => onPaid(app._id)}
              />
            )}
            <ActionButton
              label={isLockedForUser(app.status) ? "View" : "Edit"}
              icon={isLockedForUser(app.status) ? <FiEye size={16} /> : <FiEdit2 size={16} />}
              className="text-dantzBlue border border-dantzBlue bg-white hover:bg-gray-50"
              onClick={() => navigate(targetPath)}
            />
            {isLockedStatus(app.status) && (
              <ActionButton
                label="Power of attorney"
                icon={<FiFileText size={16} />}
                className="text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
                aria-expanded={powerOfAttorneyOpenId === app._id}
                onClick={() =>
                  setPowerOfAttorneyOpenId((current) => (current === app._id ? null : app._id))
                }
              />
            )}
            <ActionButton
              label={isDuplicating ? "Creating draft..." : kind === "trademark" ? "Duplicate" : kind === "design" ? "Reuse applicant data" : "Reuse applicant and inventor data"}
              icon={isDuplicating ? <Spinner size={16} /> : <FiCopy size={16} />}
              className="text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={Boolean(duplicatingId)}
              onClick={() => setDuplicateRequest({ reference: app.referenceNumber, kind, confirm: () => onDuplicate(app._id) })}
            />
            <ActionButton
              label="Delete"
              icon={<FiTrash2 size={16} />}
              className={`border bg-white ${
                isLockedForUser(app.status)
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-red-200 text-red-600 hover:bg-red-50"
              }`}
              disabled={isLockedForUser(app.status)}
              onClick={() => {
                if (window.confirm("Do you really want to delete the application?")) {
                  onDelete(app._id);
                }
              }}
            />
          </>
        }
      >
        {patOrgNote?.id === app._id && (
          <p className="mt-4 text-sm text-orange-700">{patOrgNote.message}</p>
        )}
        {resubmissionError?.id === app._id && (
          <p className="mt-3 text-sm text-red-700">{resubmissionError.message}</p>
        )}
        {isLockedStatus(app.status) && powerOfAttorneyOpenId === app._id && (
          <PowerOfAttorneyPanel kind={kind} applicationId={app._id} isAdmin={isAdmin} />
        )}
      </ApplicationCard>
    );
  }

  function renderApplications(
    items: Application[],
    page: number,
    setPage: React.Dispatch<React.SetStateAction<number>>,
    kind: ApplicationKind,
    emptyText: string,
    onPaid: (id: string) => Promise<void>,
    onDelete: (id: string) => Promise<void>,
    onDuplicate: (id: string) => Promise<void>,
  ) {
    const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

    return (
      <div className="flex flex-col gap-4">
        {loading && <p className="text-gray-500"><SpinnerText>Loading applications...</SpinnerText></p>}
        {!loading && items.length === 0 && <p className="text-gray-500">{emptyText}</p>}
        {!loading && items.length > 0 && (
          <>
            {items
              .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
              .map((app) => renderApplicationCard(app, kind, onPaid, onDelete, onDuplicate))}

            <div className="flex flex-col gap-3 mt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-600">
                {`Showing ${(page - 1) * PAGE_SIZE + 1} - ${Math.min(
                  page * PAGE_SIZE,
                  items.length,
                )} of ${items.length}`}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className="px-3 py-1 rounded-md border bg-white text-sm disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                {Array.from({ length: pageCount }, (_, i) => (
                  <button
                    key={i}
                    className={`px-3 py-1 rounded-md text-sm ${
                      page === i + 1 ? "bg-dantzBlue text-white" : "bg-white border"
                    }`}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="px-3 py-1 rounded-md border bg-white text-sm disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page >= pageCount}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <AppLayout>
      {duplicateRequest && (
        <ConfirmDialog
          message={`Would you like to create a new draft using the data from ${duplicateRequest.reference}?`}
          confirmLabel="Create new draft"
          variant="primary"
          onCancel={() => setDuplicateRequest(null)}
          onConfirm={() => {
            const request = duplicateRequest;
            setDuplicateRequest(null);
            void request.confirm();
          }}
        />
      )}
      <div className="min-h-screen">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-heading text-[30px] font-semibold tracking-[-0.01em] text-dantzBlue">
            My applications
          </h1>
          <button
            type="button"
            onClick={() => navigate("/home#new")}
            className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-[#C2610C] px-[18px] text-sm font-semibold text-white transition hover:bg-[#A8530A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzOrange"
          >
            <FiPlus size={18} />
            New application
          </button>
        </header>

        <div className="mt-6 w-full px-0">
          <div
            role="tablist"
            aria-label="Protection type"
            className="flex flex-wrap gap-1 border-b border-[#DDE2E8]"
          >
            {tabs.map((tab) => {
              const active = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(tab.key)}
                  className={`-mb-px flex h-[46px] items-center gap-2 border-b-[3px] px-4 text-[15px] ${
                    active
                      ? "border-dantzOrange font-semibold text-dantzBlue"
                      : "border-transparent font-medium text-[#4A5565] hover:text-dantzBlue"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      active ? "bg-dantzBlue text-white" : "bg-[#E6EBF0] text-gray-700"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 border border-gray-300 rounded-lg bg-white p-5">
            {duplicateError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {duplicateError}
              </div>
            )}

            {activeTab !== "release" && (
              <div className="mb-5 grid grid-cols-1 items-start gap-3 md:grid-cols-3">
                <div>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search applications"
                    className="h-10 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Reference, application number, applicant, ordering party or status</p>
                </div>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value as typeof sort)}
                  className="h-10 w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="created-asc">Created date: ascending</option>
                  <option value="created-desc">Created date: descending</option>
                  <option value="updated-asc">Updated date: ascending</option>
                  <option value="updated-desc">Updated date: descending</option>
                  <option value="reference-asc">Reference: ascending (A–Z)</option>
                  <option value="reference-desc">Reference: descending (Z–A)</option>
                </select>
                {isAdmin ? (
                  <select
                    value={ownerFilter}
                    onChange={(event) => setOwnerFilter(event.target.value)}
                    className="h-10 w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Filter by ordering party: all</option>
                    {owners.map((owner) => <option key={owner} value={owner}>{owner}</option>)}
                  </select>
                ) : <div />}
              </div>
            )}

            {activeTab === "trademark" &&
              renderApplications(
                prepareApplications(trademarkApplications),
                trademarkPage,
                setTrademarkPage,
                "trademark",
                "No trademark applications available.",
                markApplicationPaid,
                handleDelete,
                handleDuplicateTrademark,
              )}

            {activeTab === "design" &&
              renderApplications(
                prepareApplications(designApplications),
                designPage,
                setDesignPage,
                "design",
                "No design applications available.",
                markDesignApplicationPaid,
                handleDeleteDesign,
                handleDuplicateDesign,
              )}

            {activeTab === "release" && renderReleaseApplications()}

            {activeTab === "patent" &&
              renderApplications(
                prepareApplications(patentApplications),
                patentPage,
                setPatentPage,
                "patent",
                "No patent applications available.",
                markPatentApplicationPaid,
                handleDeletePatent,
                handleDuplicatePatent,
              )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
